import { PagingParameters } from "@/api/dtos";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import i18n, { SupportedLocale } from "@/i18n";
import { computeFieldMultipleprintRelatedAuthors } from "@/lib/computeFields";
import { Multipleprint } from "@/lib/types";
import { ColumnDef } from "@tanstack/react-table";
import { ChevronLeft, PlusCircle, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const MultipleLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [
    getPaginationRelatedMultipleprints,
    setPaginationRelatedMultipleprints,
  ] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedMultipleprint",
    getPaginationRelatedMultipleprints.pageSize,
    getPaginationRelatedMultipleprints.page *
      getPaginationRelatedMultipleprints.pageSize,
  );

  const { data: countRelatedMultipleprints } = useCountRelatedFields(id, type, [
    "relatedMultipleprint",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedMultipleprint",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedMultipleprint",
  );

  const [searchContent, setSearchContent] = useState("");

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const [isAddMode, setIsAddMode] = useState(false);

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedMultiple } = useGetNotRelatedResource<Multipleprint>({
    resultType: "multiple",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (multipleprintId: string) => {
      deleteRelation(multipleprintId);
    },
    [deleteRelation],
  );

  const columns: ColumnDef<Multipleprint, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "estampesMultiplesDescriptionNumeroEnregistrement",
        header: t("estampesMultiplesDescriptionNumeroEnregistrement"),
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.estampesMultiplesDescriptionNumeroEnregistrement}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "estampesMultiplesDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"multipleprint"}
            />
          );
        },
      },
      {
        accessorKey: "estampesMultiplesDescriptionTitre",
        header: t("estampesMultiplesDescriptionTitre"),
        size: 200,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.estampesMultiplesDescriptionTitre}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "author",
        header: t("tab_relatedAuthor"),
        size: 200,
        cell: ({ row }) => {
          const authors = computeFieldMultipleprintRelatedAuthors(
            row.original,
            { language: i18n.language as SupportedLocale },
          );
          return <TextCell value={authors} />;
        },
      },
      {
        accessorKey: "estampesMultiplesDescriptionDateDeCreation",
        header: t("tab_estampesMultiplesDescriptionDateDeCreation"),
        size: 200,
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.estampesMultiplesDescriptionDateDeCreation}
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          if (isAddMode) {
            return (
              <div className="flex items-center gap-1 justify-end mr-2">
                <ShowElements
                  type="multipleprint"
                  id={row.original.id}
                  elements={[
                    {
                      label: t(
                        "estampesMultiplesDescriptionNumeroEnregistrement",
                      ),
                      value:
                        row.original
                          .estampesMultiplesDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("estampesMultiplesDescriptionResume"),
                      value: row.original.estampesMultiplesDescriptionResume,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.attachments,
                      type: "image",
                    },
                  ]}
                  onAdd={() => handleCreateRelation(row.original.id)}
                />
                <Button
                  className="font-semibold flex gap-2 p-1"
                  variant="ghost"
                  onClick={() => handleCreateRelation(row.original.id)}
                  type="button"
                >
                  <PlusCircle className="w-5 h-5 cursor-pointer text-[#005D8D]" />
                </Button>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(row.original.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ShowElements
                type="multipleprint"
                id={row.original.id}
                elements={[
                  {
                    label: t(
                      "estampesMultiplesDescriptionNumeroEnregistrement",
                    ),
                    value:
                      row.original
                        .estampesMultiplesDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("estampesMultiplesDescriptionResume"),
                    value: row.original.estampesMultiplesDescriptionResume,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          );
        },
      },
    ];
  }, [handleCreateRelation, handleDeleteRelation, isAddMode, t]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between mb-2">
        <h2 className="text-lg font-semibold">{t("linkedMultipleprints")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={handleAddMode}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      <DataTable
        columns={columns}
        data={
          isAddMode ? (notRelatedMultiple?.data ?? []) : (relatedFields ?? [])
        }
        pagination={
          isAddMode
            ? {
                pagination: getPagination,
                setPage: setPagination,
                totalSize: notRelatedMultiple?.totalSize ?? 0,
              }
            : {
                pagination: getPaginationRelatedMultipleprints,
                setPage: setPaginationRelatedMultipleprints,
                totalSize: countRelatedMultipleprints?.relatedMultipleprint,
              }
        }
        options={{
          enableColumnResizing: true,
        }}
      />
    </div>
  );
};

export default MultipleLinks;
